/**
 * Invitation Service
 *
 * Handles workspace invitation creation, validation, and acceptance.
 *
 * @module services/workspace/invitationService
 */

import { getSupabaseClient, isSupabaseConfigured } from "@/config/supabase";
import { asWorkspaceRole } from "./workspaceRole";
import { info, logError } from '../logger';
import type { WorkspaceRole, WorkspaceInvitation, ServiceResult } from "@/types";
import type { TFn } from "@/utils/channelCorrelation";

/**
 * Invitation response from database
 */
interface InvitationRow {
  id: string;
  workspace_id: string;
  email: string;
  role: WorkspaceRole;
  token: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
  workspaces?: { name: string };
}

/**
 * Generate a secure random token for invitation
 * @returns 64-character hex token
 */
export const generateInvitationToken = (): string => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

/**
 * Validate invitation token format
 * @param token - Token to validate
 * @returns True if token format is valid
 */
export const validateInvitationToken = (token: string): boolean => {
  if (!token || typeof token !== "string") return false;
  return token.length === 64 && /^[a-f0-9]+$/.test(token);
};

/**
 * Check if an invitation has expired
 * @param invitation - Invitation object
 * @returns True if invitation is expired
 */
export const isInvitationExpired = (invitation: {
  expires_at?: string;
}): boolean => {
  if (!invitation.expires_at) return true;
  const expiresAt = new Date(invitation.expires_at);
  return expiresAt < new Date();
};

/**
 * Send a workspace invitation
 */
export const sendInvitation = async (
  params: {
    email: string;
    role: WorkspaceRole;
    workspaceId: string;
  },
  t: TFn,
): Promise<
  ServiceResult<{
    invitation: WorkspaceInvitation;
    /** The accept link. Present because nothing emails it — see `emailed`. */
    inviteUrl?: string;
    /** False while the email service is a placeholder: the caller must pass the link on. */
    emailed: boolean;
  }>
> => {
  const { role, workspaceId } = params;
  // Normalised ONCE, and used for both guards and the insert. The checks below
  // compared with .eq(), which is case-sensitive in Postgres, so "Foo@YDM.co"
  // sailed past a stored "foo@ydm.co" — letting an existing member, or someone
  // who already had a pending invitation, be re-invited at a different role
  // through a capitalisation the person never sees.
  const email = params.email.trim().toLowerCase();

  if (!isSupabaseConfigured()) {
    return { success: false, error: t("invite.errNotConfigured") };
  }

  const supabase = getSupabaseClient();

  try {
    // Validate input
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      return { success: false, error: t("invite.errInvalidEmail") };
    }

    if (!role || !["admin", "analyst", "viewer"].includes(role)) {
      return { success: false, error: t("invite.errInvalidRole") };
    }

    if (!workspaceId) {
      return { success: false, error: t("invite.errWorkspaceRequired") };
    }

    // Check if user already has a pending invitation
    // Pending means unaccepted AND still valid. Without the expiry filter a
    // lapsed invitation blocked its own address forever: the admin was told
    // "already has a pending invitation" for a link that could no longer be
    // used, with no way forward short of knowing to revoke it first.
    const { data: existingInvitation, error: inviteError } = await supabase
      .from("workspace_invitations")
      .select("id")
      .eq("workspace_id", workspaceId)
      .eq("email", email)
      .is("accepted_at", null)
      .gt("expires_at", new Date().toISOString())
      .maybeSingle();

    // PGRST116 covers BOTH "no rows" and "more than one row". Excluding it
    // wholesale meant two pending invitations for one address read as none,
    // and a third was minted on top. maybeSingle() is only safe here because
    // that case is now treated as what it is: an existing invitation.
    if (inviteError && inviteError.code === "PGRST116" && /multiple|more than one/i.test(inviteError.message ?? "")) {
      return { success: false, error: t("invite.errPendingExists") };
    }

    if (inviteError && inviteError.code !== "PGRST116") {
      logError("InvitationService", "Error checking existing invitation", inviteError);
      return { success: false, error: t("invite.errCheckInvitations") };
    }

    if (existingInvitation) {
      return {
        success: false,
        error: t("invite.errPendingExists"),
      };
    }

    // Check if user is already a member
    const { data: existingMembership, error: memberError } = await supabase
      .from("workspace_members")
      .select("id")
      .eq("workspace_id", workspaceId)
      .eq("email", email)
      .maybeSingle();

    if (memberError && memberError.code !== "PGRST116") {
      logError("InvitationService", "Error checking existing membership", memberError);
      return { success: false, error: t("invite.errCheckMemberships") };
    }

    if (existingMembership) {
      return {
        success: false,
        error: t("invite.errAlreadyMember"),
      };
    }

    // Generate token and expiration
    const token = generateInvitationToken();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7); // 7 days from now

    // Create invitation
    const { data, error } = await supabase
      .from("workspace_invitations")
      .insert({
        workspace_id: workspaceId,
        email,
        role,
        token,
        expires_at: expiresAt.toISOString(),
      })
      .select()
      .single();

    if (error) {
      logError("InvitationService", "Error creating invitation", error);
      return { success: false, error: error.message };
    }

    // The result is KEPT, not discarded: no email service is wired, so the
    // invitation link has to travel back to the caller for a human to pass on.
    // Discarding it is what let the UI report "Invitation sent to {email}!"
    // for a message nobody ever sent.
    // The row's ID, not its token: the route reads the token server-side after
    // proving the caller may. `token` rides along only for the local fallback
    // link, for when the route is unreachable.
    const delivery = await sendInvitationEmail({ invitationId: data.id, token }, t);

    const invitation: WorkspaceInvitation = {
      id: data.id,
      workspaceId: data.workspace_id,
      email: data.email,
      // `role` is text + CHECK in the database, so the generated type is
      // `string`. Narrowed rather than asserted — see workspaceRole.
      role: asWorkspaceRole(data.role),
      token: data.token,
      expiresAt: data.expires_at,
      acceptedAt: data.accepted_at ?? undefined,
      createdAt: data.created_at,
    };

    return {
      success: true,
      data: {
        invitation,
        inviteUrl: delivery.data?.inviteUrl,
        // The UI must be able to tell "sent" from "created, now go share this
        // link" — those are different things to say to a person.
        emailed: delivery.data?.delivered === true,
      },
    };
  } catch (err) {
    logError("InvitationService", "Unexpected error sending invitation", err);
    return { success: false, error: t("invite.errSendFailed") };
  }
};

/**
 * Validate and retrieve invitation by token
 */
export const getInvitationByToken = async (
  token: string,
  t: TFn,
): Promise<ServiceResult<InvitationRow & { is_accepted: boolean; is_expired: boolean }>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: t("invite.errNotConfigured") };
  }

  const supabase = getSupabaseClient();

  try {
    if (!validateInvitationToken(token)) {
      return { success: false, error: t("invite.errInvalidToken") };
    }

    // Use the database function to get invitation details
    // token_param, not token. PostgREST resolves an RPC by its argument NAMES, so
    // a mismatch is a 404 — the function is never reached and the error reads like
    // a missing endpoint rather than a wrong call. Verified against the running
    // PostgREST: { token } returns 404, { token_param } returns 200.
    const { data, error } = await supabase.rpc("is_valid_invitation", { token_param: token });

    if (error) {
      logError("InvitationService", "Error fetching invitation", error);
      return { success: false, error: t("invite.errFetchFailed") };
    }

    if (!data || data.length === 0) {
      return { success: false, error: t("invite.errNotFound") };
    }

    const invitation = data[0];

    if (invitation.is_accepted) {
      return { success: false, error: t("invite.errAlreadyAccepted") };
    }

    if (invitation.is_expired) {
      return { success: false, error: t("invite.errExpired") };
    }

    return {
      success: true,
      // The RPC returns role as text; narrow it the same way the insert path
      // does rather than letting an unchecked string reach WorkspaceRole.
      data: { ...invitation, role: asWorkspaceRole(invitation.role) },
    };
  } catch (err) {
    logError("InvitationService", "Error getting invitation", err);
    return { success: false, error: t("invite.errGetFailed") };
  }
};

/**
 * Accept an invitation
 */
export const acceptInvitation = async (
  token: string,
  t: TFn,
): Promise<ServiceResult<{ workspaceId: string; workspaceName: string }>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: t("invite.errNotConfigured") };
  }

  const supabase = getSupabaseClient();

  try {
    if (!validateInvitationToken(token)) {
      return { success: false, error: t("invite.errInvalidToken") };
    }

    // Check if user is authenticated
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
      return {
        success: false,
        error: t("invite.errSignInFirst"),
      };
    }

    // Use the database function to accept invitation
    // token_param — see the note in the validation path above.
    const { data, error } = await supabase.rpc("accept_invitation", { token_param: token });

    if (error) {
      logError("InvitationService", "Error accepting invitation", error);
      return { success: false, error: t("invite.errAcceptFailed") };
    }

    if (!data || data.length === 0) {
      return { success: false, error: t("invite.errAcceptFailed") };
    }

    const result = data[0];

    if (!result.success) {
      return { success: false, error: result.message };
    }

    return {
      success: true,
      data: {
        workspaceId: result.workspace_id,
        workspaceName: result.workspace_name,
      },
    };
  } catch (err) {
    logError("InvitationService", "Error accepting invitation", err);
    return { success: false, error: t("invite.errAcceptFailed") };
  }
};

/**
 * Revoke a pending invitation
 */
export const revokeInvitation = async (
  invitationId: string,
  workspaceId: string,
  t: TFn,
): Promise<ServiceResult> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: t("invite.errNotConfigured") };
  }

  const supabase = getSupabaseClient();

  try {
    // .select() so the reply carries the rows actually deleted. Without it
    // PostgREST answers 204 with no error when RLS filters the match to zero —
    // so a revoke the caller was not allowed to perform, or one aimed at
    // another workspace's invitation, resolved { success: true } and the UI
    // dropped the row from the pending list while the invitation stayed live
    // and usable. An empty result is a refusal, not a success.
    const { data, error } = await supabase
      .from("workspace_invitations")
      .delete()
      .eq("id", invitationId)
      .eq("workspace_id", workspaceId)
      .select("id");

    if (error) {
      logError("InvitationService", "Error revoking invitation", error);
      return { success: false, error: error.message };
    }

    if (!data || data.length === 0) {
      logError("InvitationService", "Revoke matched no invitation", { invitationId, workspaceId });
      return {
        success: false,
        error: t("invite.errRevokeNotAllowed"),
      };
    }

    return { success: true };
  } catch (err) {
    logError("InvitationService", "Error revoking invitation", err);
    return { success: false, error: t("invite.errRevokeFailed") };
  }
};

/**
 * Get all pending invitations for a workspace
 */
export const getPendingInvitations = async (
  workspaceId: string,
  t: TFn,
): Promise<ServiceResult<InvitationRow[]>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: t("invite.errNotConfigured") };
  }

  const supabase = getSupabaseClient();

  try {
    const { data, error } = await supabase
      .from("workspace_invitations")
      .select("*, workspaces(name)")
      .eq("workspace_id", workspaceId)
      .is("accepted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      logError("InvitationService", "Error fetching pending invitations", error);
      return { success: false, error: error.message };
    }

    return { success: true, data: (data as InvitationRow[]) || [] };
  } catch (err) {
    logError("InvitationService", "Error getting pending invitations", err);
    return { success: false, error: t("invite.errGetPendingFailed") };
  }
};

/**
 * Send an invitation email through the server route, and report whether it went.
 *
 * The SMTP transport is server-side (nodemailer), so this posts to
 * `/api/invitations/send` and returns what the server says. Two things the route
 * deliberately does NOT accept from here, both security rather than style:
 *
 *  - **the token.** The token is the credential that grants workspace access.
 *    Only the invitation's ID crosses the wire; the server reads the token from
 *    the row after proving the caller is an admin of that workspace.
 *  - **the link's base URL.** It comes from `APP_PUBLIC_URL` on the server, not
 *    from this origin, so a credential-bearing link cannot be pointed elsewhere.
 *
 * `delivered: false` is still a normal answer, not a failure: SMTP is optional
 * (prod deliberately leaves `SMTP_HOST` unset), and the caller needs the link
 * either way so the UI can say "created — now share this" instead of claiming a
 * message was sent. That distinction is what `emailed` carries in sendInvitation.
 *
 * If the route itself cannot be reached — an older deployment without the
 * function, or a dev server without the plugin — this falls back to the link it
 * can build locally, with `delivered: false`. Failing to send an email must not
 * cost the admin the invitation they just created.
 */
export const sendInvitationEmail = async (
  params: {
    invitationId: string;
    /** Only used for the local fallback link when the route is unreachable. */
    token: string;
  },
  t: TFn,
): Promise<ServiceResult<{ inviteUrl: string; delivered: boolean }>> => {
  const { invitationId, token } = params;

  if (!isSupabaseConfigured()) {
    return { success: false, error: t("invite.errNotConfigured") };
  }

  const localUrl = `${window.location.origin}/invite/accept?token=${encodeURIComponent(token)}`;

  try {
    const { data: session } = await getSupabaseClient().auth.getSession();
    const accessToken = session.session?.access_token;
    if (!accessToken) {
      return { success: false, error: t("invite.errNotConfigured") };
    }

    const response = await fetch("/api/invitations/send", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ invitation_id: invitationId }),
    });

    if (!response.ok) {
      // The invitation exists; only its announcement failed. Hand back a link.
      info("InvitationService", "Invitation email route refused; returning the link instead", {
        status: response.status,
      });
      return { success: true, data: { inviteUrl: localUrl, delivered: false } };
    }

    const payload = (await response.json()) as { inviteUrl?: string; delivered?: boolean };
    return {
      success: true,
      data: {
        inviteUrl: payload.inviteUrl ?? localUrl,
        delivered: payload.delivered === true,
      },
    };
  } catch (err) {
    // Network-level failure. Same reasoning as the !ok branch above.
    logError("InvitationService", "Error sending invitation email", err);
    return { success: true, data: { inviteUrl: localUrl, delivered: false } };
  }
};


export default {
  sendInvitation,
  getInvitationByToken,
  acceptInvitation,
  revokeInvitation,
  getPendingInvitations,
  sendInvitationEmail,
  generateInvitationToken,
  validateInvitationToken,
  isInvitationExpired,
};
